/**
 * Types voor module E6 Goedkeuringen-inbox.
 *
 * Pure read-aggregator over bestaande tabellen (verlof, declaraties,
 * weekstaten, mutaties, onboarding, werkbonnen). Geen eigen schema.
 *
 * Deze DTOs spiegelen de minimale velden die de tabbed inbox-UI nodig heeft
 * — naam medewerker, status, identificerende info, datum, en een
 * `detailHref` voor navigatie naar de bestaande detail-pagina van de
 * onderliggende module.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import type { DeclaratieStatus, DeclaratieType } from './declaraties'
import type { MutatieType } from './selfservice'
import type { WerkbonStatus, WerkbonType, DayEntryStatus } from './werkbonnen'
import type { UitrustingBestellingStatus } from './uitrusting'

// Verlof-types worden niet uit `./verlof` geïmporteerd (die file bevat alleen
// balance-DTOs). We kopiëren de string-unions zoals ze in de Prisma-enum staan.
export type LeaveStatusInInbox =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GECLASSIFICEERD'
  | 'DEFINITIEF'
  | 'OVERRULED'
  | 'AFGEKEURD'
  | 'INGETROKKEN'
  | 'AANGEVRAAGD'   // V1 legacy
  | 'GOEDGEKEURD'   // V1 legacy
  | 'AFGEWEZEN'     // V1 legacy

export type VerlofMethodeInInbox = 'VERLOF' | 'ATV' | 'TVT' | 'BIJZONDER'

// ─── Categorieën ────────────────────────────────────────────────────────────

export const GOEDKEURINGEN_INBOX_CATEGORIE = [
  'verlof',
  'declaraties',
  'weekstaten',
  'mutaties',
  'onboarding',
  'werkbonnen',
  'uitrusting',
] as const
export type GoedkeuringenInboxCategorie =
  (typeof GOEDKEURINGEN_INBOX_CATEGORIE)[number]

export const GOEDKEURINGEN_INBOX_CATEGORIE_LABELS: Record<
  GoedkeuringenInboxCategorie,
  string
> = {
  verlof:      'Verlof',
  declaraties: 'Declaraties',
  weekstaten:  'Weekstaten',
  mutaties:    'Mutatieverzoeken',
  onboarding:  'Onboarding',
  werkbonnen:  'Werkbonnen',
  uitrusting:  'Uitrusting',
}

// ─── Gedeeld: minimale employee-info voor lijst-cards ───────────────────────

export interface GoedkeuringenInboxEmployeeRef {
  id: string
  firstName: string
  lastName: string
  employeeNumber: string
}

// ─── Per-categorie item-DTO ────────────────────────────────────────────────

export interface GoedkeuringenInboxVerlofItem {
  id:           string
  employee:     GoedkeuringenInboxEmployeeRef
  /** null als V1-aanvraag zonder methode */
  methode:      VerlofMethodeInInbox | null
  status:       LeaveStatusInInbox
  /**
   * Spec 34 — intrekking-keten-status op een DEFINITIEF verlof. `GEEN` =
   * normaal approval-item (geen lopende intrekking). `AANGEVRAAGD` = TL-stap,
   * `TL_GOEDGEKEURD` = HR-stap; de UI toont dan een "Intrekking"-badge en de
   * juiste inline-actie.
   */
  intrekkingStatus: 'GEEN' | 'AANGEVRAAGD' | 'TL_GOEDGEKEURD' | 'INGETROKKEN' | 'AFGEWEZEN'
  /**
   * Deel 6 — `true` als op dit DEFINITIEF verlof een wijzigingsverzoek loopt
   * (`wijzigingAangevraagdOp` gezet, nog niet beoordeeld). De UI toont dan een
   * "Wijzigingsverzoek"-badge en een deep-link naar de detailpagina (HR
   * beoordeelt de voorgestelde nieuwe periode daar); geen inline approve.
   */
  isWijzigingsverzoek: boolean
  startDate:    string
  endDate:      string | null
  hours:        number | null
  // FO-309 P1 — dagdeel-/urenverlof-tijden zodat ook de aggregatie-inbox de tijd
  // ("Hele dag" / "09:00 - 13:00") toont, niet alleen het datumbereik.
  isPartialDay: boolean
  startTijd:    string | null
  eindTijd:     string | null
  submittedAt:  string | null
  detailHref:   string
}

export interface GoedkeuringenInboxDeclaratieItem {
  id:           string
  employee:     GoedkeuringenInboxEmployeeRef
  type:         DeclaratieType
  omschrijving: string
  bedrag:       number
  datum:        string
  status:       DeclaratieStatus
  submittedAt:  string | null
  detailHref:   string
}

export interface GoedkeuringenInboxWeekstaatItem {
  id:           string
  employee:     GoedkeuringenInboxEmployeeRef
  year:         number
  week:         number
  /** null als snapshot nog niet ingevuld bij INGEDIEND */
  totalUren:    number | null
  submittedAt:  string | null
  detailHref:   string
}

export interface GoedkeuringenInboxMutatieItem {
  id:           string
  employee:     GoedkeuringenInboxEmployeeRef
  type:         MutatieType
  ingediendOp:  string
  detailHref:   string
}

// C0 — Prisma OnboardingStatus is uitgebreid met meer waarden (WACHT_OP_*,
// IN_CONTROLE, ASSETS_TOEWIJZEN, GEREED_VOOR_START, ACTIEF). De `OnboardingStatus`
// in `./onboarding` is nog de v1-narrow union (4 waarden); we inline-widen hier
// zodat de inbox-DTO direct uit Prisma kan worden gevuld zonder cast.
// TODO PR C9: lees deze nieuwe statussen op het dashboard en consolideer naar
// één bron (`OnboardingStatusValue` uit `@streep/utils/onboarding-helpers`).
export type GoedkeuringenInboxOnboardingStatus =
  | 'CONCEPT'
  | 'WACHT_OP_MEDEWERKER'
  | 'IN_BEHANDELING'
  | 'IN_CONTROLE'
  | 'WACHT_OP_HANDTEKENING'
  | 'ASSETS_TOEWIJZEN'
  | 'GEREED_VOOR_START'
  | 'ACTIEF'
  | 'AFGEROND'
  | 'GEANNULEERD'

/**
 * batch-59 onboarding-review (FO-437 §B) — één ingediende onboarding-stap die
 * een goedkeurbesluit kent (`OnboardingStap.status === 'INGEDIEND'`). Alleen
 * deze stappen zijn inline af te handelen; de rest van de onboarding blijft
 * deep-link. `titel` is het leesbare stap-label voor de knoprij.
 */
export interface GoedkeuringenInboxOnboardingStap {
  id:     string
  type:   string
  titel:  string
}

export interface GoedkeuringenInboxOnboardingItem {
  id:           string
  employee:     GoedkeuringenInboxEmployeeRef
  status:       GoedkeuringenInboxOnboardingStatus
  startDatum:   string | null
  stappenOpen:  number
  stappenTotaal: number
  /**
   * FO-437 §B — de stappen met een goedkeurbesluit (INGEDIEND). Leeg = niets
   * inline af te handelen; de UI toont dan alleen de deep-link met uitleg.
   */
  besluitbareStappen: GoedkeuringenInboxOnboardingStap[]
  detailHref:   string
}

/**
 * PR-2 (spec 07 §1.8) — werkbon-niveau goedkeur-item.
 *
 * Het oude shape (werkbonNumber/orderNumber/projectLeader/weekNr) wordt
 * behouden voor compat met de bestaande backoffice-tabel. De nieuwe velden
 * (klant, omschrijving, ingediendOp, aantalDagen, totaalUren, periodeVan/Tot,
 * dayEntries) zijn `optional` zodat oude builds blijven werken — sub-agent 3
 * frontend gebruikt de nieuwe shape volledig.
 */
export interface GoedkeuringenInboxWerkbonItem {
  id:            string
  werkbonNumber: string
  orderNumber:   string
  /** null als geen project-leider gekoppeld */
  projectLeader: GoedkeuringenInboxEmployeeRef | null
  type:          WerkbonType
  status:        WerkbonStatus
  weekNr:        string
  detailHref:    string
  // PR-2 spec §1.8 — uitbreidingen voor de nieuwe werkbon-tabel-UI
  klant?:         string
  omschrijving?:  string
  ingediendOp?:   string | null
  ingediendDoor?: string | null
  aantalDagen?:   number
  totaalUren?:    number
  periodeVan?:    string
  periodeTot?:    string
  // Dag-uitsplitsing voor de werkbon-review (uren/reistijd per dag). Een dag
  // wordt uitsluitend gesloten via het indienen door de monteur; de inbox toont
  // alleen werkbonnen met goedkeuringStatus = WACHT_OP_INTERN.
  dayEntries?:    Array<{
    id?:         string
    date:        string
    uren:        number
    reisMinuten: number
    reisKm:      number
    status?:     DayEntryStatus
  }>
}

export interface GoedkeuringenInboxUitrustingItem {
  id:            string
  employee:      GoedkeuringenInboxEmployeeRef
  status:        UitrustingBestellingStatus
  creditsTotaal: number
  aantalRegels:  number
  ingediendOp:   string | null
  detailHref:    string
}

// ─── Generieke approval-bronnen (registry — centraliseren-spec Deel 1-2) ──────
//
// Naast de zeven "legacy" categorieën hierboven (met bespoke DTO's + rendering)
// registreren extra interne goedkeur-bronnen zich GENERIEK: elke bron levert een
// `GoedkeuringenInboxGenericItem` en verschijnt automatisch in Goedkeuringen
// (count + lijst + act-in-place/deep-link) zonder dat de aggregator-core
// verandert. Eén registry-entry toevoegen = een nieuwe bron erbij (Deel 0 §1).
//
// Klant-akkoord (ClientPortalUser) hoort hier NIET bij — dat blijft extern in
// het klantenportaal (Deel 0 §4).

// Kwaliteit-controlelijsten zijn (nog) een zelf-afronding-workflow zonder
// aparte goedkeur-gate (geen KWALITEIT.canApprove-endpoint) → geen bron om te
// centraliseren. Zodra die module een review-stap krijgt, is het één extra
// registry-entry. Klant-akkoord blijft extern (Deel 0 §4).
export const GOEDKEURINGEN_EXTRA_CATEGORIE = [
  'keuzebudget',
  'reisuren',
  'verzuim',
  'formulieren',
  'veiligheid',
  'opleidingen',
  'payroll-correctie',
  'inkoop',
  'inkoop-aanvraag',
  'intercompany',
  'vakantie-campagne',
  'facturatie-intern',
  'certificaat-aanvraag',
  // FO-046-P2 (batch-16) — portal document-upload met TL/HR-goedkeuring.
  'document-aanvraag',
  'planning-wijziging',
  // "Gesprek aanvragen" uit de portal (ContactRequest). Niet-vertrouwelijke open
  // verzoeken die opgepakt moeten worden; deep-link (geen inline approve/reject).
  'contactverzoek',
  'min-uren',
  // Verkoopfacturen-mailbox: AI-voorgevulde concepten die op review wachten
  // (spiegelt de 'inkoop'-bron; goedkeuren gebeurt op het review-scherm).
  'verkoopfactuur',
  // batch-59 onboarding-review (FO-437) — een door de klant ingediende
  // relatie-onboarding (ClientOnboarding, WACHT_OP_REVIEW) met minstens één
  // klant-sectie die na de laatste indiening nog niet is goedgekeurd. Module
  // CRM; inline "alles goedkeuren" (A2), afkeuren per sectie op het
  // reviewscherm (een reden hoort bij één sectie, niet bij de hele indiening).
  'relatie-onboarding',
  // FO-508 (spec 208) — losse koppeling telefoon/simkaart (telefoon zonder
  // simkaart of andersom): goedkeuren door HR of directie (ADMIN), nooit de
  // aanvrager zelf. Inline approve/reject naar /middel-uitzonderingen/:id/...
  'telefoon-uitzondering',
  // batch-69 samenstellen (FO-516 §C6) — niet ingeleverd bij een innamelijst:
  // HR beoordeelt per item. Inline goedkeuren = verrekenen tegen het voorstel,
  // afwijzen = afboeken met reden; nasturen of een ander bedrag op de lijst.
  'inname-hr',
  // batch-69 magazijn-cijfers (FO-521 §C10) — adviesregels van het besteladvies
  // (stand GOEDKEURING_HANDMATIG): inline goedkeuren/afkeuren per regel; zodra
  // alle regels van een advies beoordeeld zijn, rondt het systeem het af.
  'besteladvies',
  // batch-69 magazijn-cijfers (FO-520 §C13) — tellingcorrecties boven de
  // vier-ogen-drempel: tweede goedkeuring door VOORRAAD.canApprove, nooit door de
  // teller of de indiener (de API weigert dat, ook inline).
  'tellingcorrectie',
] as const
export type GoedkeuringenExtraCategorie =
  (typeof GOEDKEURINGEN_EXTRA_CATEGORIE)[number]

export const GOEDKEURINGEN_EXTRA_CATEGORIE_LABELS: Record<
  GoedkeuringenExtraCategorie,
  string
> = {
  'keuzebudget':       'Keuzebudget',
  'reisuren':          'Reisuren-afwijking',
  'verzuim':           'Verzuim',
  'formulieren':       'Formulieren',
  'veiligheid':        'Veiligheidsdocumenten',
  'opleidingen':       'Opleidingsaanvragen',
  'payroll-correctie': 'Payroll-correcties',
  'inkoop':            'Inkoop',
  'inkoop-aanvraag':   'Inkoopaanvragen',
  'intercompany':      'Intercompany',
  'vakantie-campagne': 'Vakantie-campagne',
  'facturatie-intern': 'Facturatie (intern akkoord)',
  'certificaat-aanvraag': 'Certificaat-aanvragen',
  'document-aanvraag':  'Document-aanvragen',
  'planning-wijziging': 'Planning-wijzigingen',
  'contactverzoek':     'Contactverzoeken',
  'min-uren':           'Min-uren',
  'verkoopfactuur':     'Verkoopfacturen (mail)',
  'relatie-onboarding': 'Relatie-onboarding',
  'telefoon-uitzondering': 'Telefoon/simkaart los koppelen',
  'inname-hr': 'Inname ter goedkeuring HR',
  'besteladvies':          'Besteladvies',
  'tellingcorrectie':      'Tellingcorrecties',
}

/**
 * Eén genormaliseerd open-goedkeur-item van een generieke bron. Subject is de
 * medewerker of (bv. inkoop/facturatie) een ander onderwerp; `bedrag`/`uren`
 * zijn optioneel en alleen gevuld waar relevant. `stapBadge` toont de stap/rol
 * (TL/HR) of een status-label.
 */
export interface GoedkeuringenInboxGenericItem {
  id:           string
  categorie:    GoedkeuringenExtraCategorie
  subjectNaam:  string
  subjectSub:   string | null
  titel:        string
  samenvatting: string
  bedrag:       number | null
  uren:         number | null
  datum:        string | null
  stapBadge:    string | null
  detailHref:   string
  /**
   * batch-63 certificaat-mailbox (FO-469) — per item uitzetten van act-in-place
   * binnen een bron die het wel kan (bv. een certificaat uit de mailbox moet in
   * de vergelijkweergave beoordeeld worden). Afwezig = de vlag van de bucket.
   */
  inlineApprove?: boolean
  inlineReject?:  boolean
}

/**
 * Eén bucket per generieke bron in de aggregator-response. `inlineApprove`/
 * `inlineReject` zeggen of de inbox act-in-place mag tonen (delegatie naar het
 * module-endpoint); zo niet, valt de UI terug op de deep-link (`detailHref`).
 */
export interface GoedkeuringenInboxExtraBucket {
  categorie:     GoedkeuringenExtraCategorie
  label:         string
  count:         number
  items:         GoedkeuringenInboxGenericItem[]
  inlineApprove: boolean
  inlineReject:  boolean
}

// ─── Aggregator-response ─────────────────────────────────────────────────────

/**
 * Per-categorie permission-vlag — true als huidige user de tab/categorie
 * mag zien (canApprove op de onderliggende module). Frontend gebruikt dit
 * om tabs te verbergen.
 */
export interface GoedkeuringenInboxPermissies {
  verlof:      boolean
  declaraties: boolean
  weekstaten:  boolean
  mutaties:    boolean
  onboarding:  boolean
  werkbonnen:  boolean
  uitrusting:  boolean
}

export interface GoedkeuringenInboxBucket<T> {
  count: number
  items: T[]
}

/**
 * batch-59 onboarding-review (FO-437 §B6) — waarom ziet deze gebruiker (n)iets?
 *
 * De aanleiding: de PO zag een LEGE onboarding-tab zonder enige uitleg. Dat kan
 * twee heel verschillende dingen betekenen - "er is niets te doen" of "je mag
 * het niet zien / het valt buiten je team" - en zonder dit blokje is dat vanaf
 * het scherm niet te onderscheiden. De UI rendert er de lege-staat mee; de
 * bijbehorende SQL-diagnose (docs/specs/137-diagnose-onboarding-goedkeuringen.sql)
 * geeft dezelfde feiten per user voor een prod-analyse.
 */
export interface GoedkeuringenInboxScopeDiagnose {
  /**
   * Hoe de zichtbaarheid is bepaald:
   *  - `broad`      alles, alle teams (ADMIN/directie en overige canApprove-rollen)
   *  - `hr`         HR-stap, geen team-filter
   *  - `teamleider` alleen het eigen team
   */
  modus: 'broad' | 'hr' | 'teamleider'
  /**
   * Aantal medewerkers binnen de team-scope. `null` = geen team-filter
   * (broad/hr). `0` bij een teamleider zonder afdeling: die ziet per definitie
   * niets - precies het geval dat als "lege tab" werd ervaren.
   */
  teamOmvang: number | null
}

export interface GoedkeuringenInboxResponse {
  permissies:   GoedkeuringenInboxPermissies
  /** Som van counts over alle categorieën waar user rechten heeft. */
  aantalTotaal: number
  /** FO-437 §B6 — permissie-/scope-uitleg voor de lege-staat. */
  scope:        GoedkeuringenInboxScopeDiagnose

  verlof:       GoedkeuringenInboxBucket<GoedkeuringenInboxVerlofItem>
  declaraties:  GoedkeuringenInboxBucket<GoedkeuringenInboxDeclaratieItem>
  weekstaten:   GoedkeuringenInboxBucket<GoedkeuringenInboxWeekstaatItem>
  mutaties:     GoedkeuringenInboxBucket<GoedkeuringenInboxMutatieItem>
  onboarding:   GoedkeuringenInboxBucket<GoedkeuringenInboxOnboardingItem>
  werkbonnen:   GoedkeuringenInboxBucket<GoedkeuringenInboxWerkbonItem>
  uitrusting:   GoedkeuringenInboxBucket<GoedkeuringenInboxUitrustingItem>

  /**
   * Generieke extra bronnen (registry). Eén bucket per bron waarvoor de
   * gebruiker canApprove heeft; leeg als de gebruiker geen extra bronnen mag
   * zien. `aantalTotaal` telt deze counts mee (Deel 4 - badge over alle bronnen).
   */
  extraBronnen: GoedkeuringenInboxExtraBucket[]
}

export interface GoedkeuringenInboxAantalTotaalResponse {
  aantalTotaal: number
}

// ─── Zod-schemas (request-validatie) ─────────────────────────────────────────

export const goedkeuringenInboxQuerySchema = z.object({
  topN: z.coerce.number().int().min(1).max(50).default(10),
})

export type GoedkeuringenInboxQuery = z.infer<typeof goedkeuringenInboxQuerySchema>

/**
 * Bulk-action body. De backend mapt categorie → per-module approve-endpoint en
 * past de actie toe per id. Het resultaat is per item: ok of error-detail.
 */
export const goedkeuringenInboxBulkActionSchema = z.object({
  // Legacy categorie of een generieke extra-bron-categorie (registry). De
  // backend routeert extra-categorieën via de registry-`act`, de legacy via de
  // bestaande per-module-mapper.
  categorie: z.enum([
    ...GOEDKEURINGEN_INBOX_CATEGORIE,
    ...GOEDKEURINGEN_EXTRA_CATEGORIE,
  ]),
  actie: z.enum(['approve', 'reject']),
  ids: z.array(idParam()).min(1).max(100),
  /** Optionele toelichting / reden — verplicht bij reject. */
  toelichting: z.string().trim().max(2000).optional(),
})

export type GoedkeuringenInboxBulkAction =
  z.infer<typeof goedkeuringenInboxBulkActionSchema>

export interface GoedkeuringenInboxBulkActionResultItem {
  id:      string
  ok:      boolean
  error?:  string
}

export interface GoedkeuringenInboxBulkActionResult {
  totaal:     number
  geslaagd:   number
  mislukt:    number
  items:      GoedkeuringenInboxBulkActionResultItem[]
}

// ─── Afgehandeld (besliste goedkeuringen — spec 2026-06-03) ──────────────────

export const GOEDKEURING_UITKOMST = ['goedgekeurd', 'afgekeurd'] as const
export type GoedkeuringUitkomst = (typeof GOEDKEURING_UITKOMST)[number]

/**
 * Eén genormaliseerde besliste-goedkeuring-rij, gedeeld door de "Alles"-weergave
 * en de per-categorie tabel. `besluitDoor` is al geresolved naar een naam.
 * Onboarding heeft geen approver → `besluitDoor` is daar `null`.
 */
export interface GoedkeuringAfgehandeldItem {
  id:            string
  categorie:     GoedkeuringenInboxCategorie
  subjectNaam:   string         // medewerker-naam, of projectleider/klant (werkbon)
  subjectSub:    string | null  // employeeNumber, of werkbonNumber
  identificatie: string         // kort label: "Week 22 2026", "€ 145,00", "WB-..."
  omschrijving:  string
  uitkomst:      GoedkeuringUitkomst  // bepaalt badge-kleur
  uitkomstLabel: string         // "Goedgekeurd" / "Afgekeurd" / "Afgerond" / "Geannuleerd"
  besluitDoor:   string | null
  besluitOp:     string | null  // ISO
  detailHref:    string
}

export interface GoedkeuringAfgehandeldResponse {
  items:   GoedkeuringAfgehandeldItem[]
  totaal:  number
  page:    number
  limit:   number
  hasMore: boolean
}

export const goedkeuringAfgehandeldQuerySchema = z.object({
  categorie:    z.enum([...GOEDKEURINGEN_INBOX_CATEGORIE, 'alles'] as [string, ...string[]]).default('alles'),
  uitkomst:     z.enum(['alle', ...GOEDKEURING_UITKOMST] as [string, ...string[]]).default('alle'),
  van:          z.string().optional(),  // YYYY-MM-DD
  tot:          z.string().optional(),  // YYYY-MM-DD
  medewerkerId: idParam().optional(),
  zoek:         z.string().trim().max(200).optional(),
  page:         z.coerce.number().int().min(1).default(1),
  limit:        z.coerce.number().int().min(10).max(100).default(25),
})

export type GoedkeuringAfgehandeldQuery = z.infer<typeof goedkeuringAfgehandeldQuerySchema>
export type GoedkeuringAfgehandeldCategorieFilter = GoedkeuringenInboxCategorie | 'alles'

// ─── Door mij goedgekeurd, ligt nu bij HR (Spec 6 §4) ────────────────────────
//
// Read-only tracking-overzicht: items die de INGELOGDE gebruiker zelf als
// teamleider heeft goedgekeurd en die nu in de HR-stap wachten
// (`GOEDGEKEURD_TEAMLEIDER`). Alleen de twee-staps-categorieën (verlof +
// declaraties) hebben een TL→HR-doorzet; de overige categorieën kennen die
// tussenstap niet en doen hier dus niet mee.
//
// "door mij" = `teamleiderApprovedBy` (verlof) / `teamleiderReviewedBy`
// (declaratie) == currentUser.id. `sindsWanneer` = het TL-goedkeur-moment.

export type GoedkeuringDoorMijCategorie = Extract<
  GoedkeuringenInboxCategorie,
  'verlof' | 'declaraties'
>

export interface GoedkeuringDoorMijItem {
  id:            string
  categorie:     GoedkeuringDoorMijCategorie
  employee:      GoedkeuringenInboxEmployeeRef
  /** Kort label: periode (verlof) of bedrag (declaratie). */
  identificatie: string
  omschrijving:  string
  /** Huidige workflow-status (altijd GOEDGEKEURD_TEAMLEIDER in dit overzicht). */
  status:        string
  /** ISO — wanneer de ingelogde gebruiker dit als TL goedkeurde. */
  sindsWanneer:  string | null
  detailHref:    string
}

export interface GoedkeuringDoorMijResponse {
  items:  GoedkeuringDoorMijItem[]
  totaal: number
}
